import React, { useState } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import striptags from 'striptags';
import { createGlobalStyle } from 'styled-components';
import { getFrameAttrs } from 'utils/embed';
import { logError } from 'utils/logging';
import { GlobalContext } from 'globalContext';
import { Link } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { themeColors, ThemeProvider, FullscreenProvider, PlayerHeader, PlayerFooter, Modal, ShareSheet } from 'msp-components';
import ErrorBoundary from 'components/ErrorBoundary';
import templateOptionsPropTypes from 'components/templateOptionsPropTypes';
import styles from './Wrapper.module.scss';

const getLinkColor = props => {
  if (props.brandColor) {
    return props.brandColor;
  } else {
    return props.theme === 'dark' ? themeColors.themeDarkPrimary : themeColors.themeLightPrimary;
  }
};

const GlobalStyle = createGlobalStyle`
  a,
  a:link,
  a:visited,
  a:focus,
  a:hover {
    color: ${props => getLinkColor(props)};
  }
`;

const Wrapper = ({
  project,
  templateOptions,
  navigation,
  titlePaths,
  itemsOrdered,
  itemsById,
  isHomepage,
  activeItem,
  activeMenuItem,
  children,
  containerSize,
  onContainerResize = () => {},
}) => {
  const {
    theme,
    accentColor,
    fontHeading,
    fontBody,
    fontCaption,
    fontUi,
    logo,
    logoLink,
    footerText,
    promo1Image,
    promo2Image,
    promo1Link,
    promo2Link,
    promoText,
  } = templateOptions;

  const { isEmbedded, parentUrl } = getFrameAttrs();
  const { internalDomains } = project || {};

  let isInternalEmbed = false;
  if (isEmbedded && parentUrl && internalDomains && internalDomains.length) {
    try {
      const url = new URL(parentUrl);
      if (internalDomains.includes(url.hostname)) {
        isInternalEmbed = true;
      }
    } catch (e) {
      console.error(e);
      logError(e);
    }
  }

  const [currentItemTitle, setCurrentItemTitle] = useState(null);
  const [currentSubItem, setCurrentSubItem] = useState(null);

  // ShareSheet
  const [showShareSheet, setShowShareSheet] = useState(false);
  const toggleShareSheet = () => setShowShareSheet(!showShareSheet);
  activeItem = activeItem || {};
  const projectUrl = urlJoin(window.location.origin, window.BASE_PATH);
  let embedOptions;
  let shareOptions;

  if (!isHomepage) {
    embedOptions = [
      { id: 1, url: window.location.href, label: `Current Item: ${currentItemTitle || activeItem.name}` },
      { id: 2, url: projectUrl, label: `Project: ${project.name}`, isDefault: true },
    ];
    shareOptions = !project.shareUrl ? embedOptions.map(opt => ({ ...opt, isDefault: opt.id === 1 })) : undefined;
  }

  const shareSheetProps = {
    shareUrl: project.shareUrl || projectUrl,
    shareOptions,
    embedUrl: projectUrl,
    embedOptions,
    title: project.name,
    description: striptags(project.shortDescription),
    publisher: project.publisher,
    onShareClick: toggleShareSheet,
    allowEmbed: true,
  };

  const linkStyle = { textDecoration: 'none' };
  if (accentColor) linkStyle.color = accentColor;

  const renderHeaderImage = () => {
    const logoHeight = (containerSize && containerSize.width < 450) ? 20 : 30;
    const node = logo
      ? <img src={logo} style={{ display: 'block', height: logoHeight }} alt={project.publisher.name} />
      : <h1 className={styles.logoAlt}>{project.publisher.name}</h1>;
    return logoLink
      ? <a href={logoLink} target="_blank" rel="noopener noreferrer" style={linkStyle}>{node}</a>
      : <Link to="/" style={linkStyle}>{node}</Link>;
  };

  const bgColor = theme === 'dark' ? themeColors.grayCoal : themeColors.grayNickel;
  const promoImageMaxHeight = (containerSize && containerSize.width < 450) ? 30 : 40;
  const renderPromoImage = (image, link) => {
    if (!image) return null;
    let node = <img style={{ display: 'block', maxHeight: promoImageMaxHeight }} src={image} alt="" />;
    if (link) node = <a href={link} target="_blank" rel="noopener noreferrer">{node}</a>;
    return <div style={{ marginLeft: 5 }}>{node}</div>;
  };
  const showPromos = !!(promo1Image || promo2Image);

  const header = isInternalEmbed ? (
    !navigation && <div style={{ height: 1, flex: '0 0 1px', backgroundColor: bgColor }} />
  ) : (
    <PlayerHeader
      imageUrl={logo}
      renderImage={renderHeaderImage}
    >
      {showPromos && (
        <div style={{ display: 'flex', alignItems: 'center', marginRight: -5 }}>
          {!!promoText && <span style={{ marginRight: 5 }}>{promoText}</span>}
          {renderPromoImage(promo1Image, promo1Link)}
          {renderPromoImage(promo2Image, promo2Link)}
        </div>
      )}
    </PlayerHeader>
  );

  const fontConfig = {
    heading: fontHeading,
    body: fontBody,
    caption: fontCaption,
    ui: fontUi,
  };

  return (
    <ThemeProvider
      theme={theme}
      brandColor={accentColor || undefined}
      fontConfig={fontConfig}
    >
      <GlobalStyle theme={theme} brandColor={accentColor || undefined} />
      <GlobalContext.Provider value={{
        titlePaths,
        itemsOrdered,
        itemsById,
        isInternalEmbed,
        toggleShareSheet,
        showShareSheet,
        activeMenuItem,
        currentItemTitle,
        setCurrentItemTitle,
        currentSubItem,
        setCurrentSubItem,
        project,
      }}>
        <FullscreenProvider>
          <div className={styles.container}>
            <ResizeDetector
              handleWidth
              handleHeight
              onResize={onContainerResize}
              refreshMode="debounce"
              refreshRate={500}
            />
            {header}
            {navigation}
            <div className={styles.titleContainer}>
              <ErrorBoundary>
                {children}
              </ErrorBoundary>

              <Modal
                overlayStyle={{
                  position: 'fixed',
                  zIndex: 1,
                }}
                isOpen={showShareSheet}
                onRequestClose={() => setShowShareSheet(false)}
              >
                <div style={{ padding: '15px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShareSheet {...shareSheetProps} />
                </div>
              </Modal>
            </div>
            {!isInternalEmbed && <PlayerFooter content={footerText} />}
          </div>
        </FullscreenProvider>
      </GlobalContext.Provider>
    </ThemeProvider>
  );
};

Wrapper.propTypes = {
  templateOptions: PropTypes.shape(templateOptionsPropTypes),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    internalDomains: PropTypes.arrayOf(PropTypes.string),
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  titlePaths: PropTypes.object,
  itemsOrdered: PropTypes.arrayOf(PropTypes.string),
  itemsById: PropTypes.object,
  isHomepage: PropTypes.bool,
  activeItem: PropTypes.object,
  activeMenuItem: PropTypes.string,
  navigation: PropTypes.node,
  containerSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  children: PropTypes.node,
  onContainerResize: PropTypes.func,
};

export default Wrapper;
